import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Hammer from 'react-hammerjs';
import useComponentSize from '@rehooks/component-size';
import 'resize-observer-polyfill';

import { useTheme } from '../../ThemeContext';

import AnnotationCanvasLegend from '../AnnotationCanvasLegend';

import { Box, Holder, CanvasHolder, Canvas } from './AnnotationCanvas.style';

const AnnotationCanvas = ({
  mediaWidth,
  mediaHeight,
  objects,
  selectedObject,
  handleSelectObject = () => {},
  handleChangeAnnotation = () => {},
  handleSetVisibleImageMenu = () => {},
}) => {
  const theme = useTheme();
  const holderRef = useRef();
  const canvasRef = useRef();
  let canvasSize = useComponentSize(holderRef);
  const [canvasOptions] = useState({
    area: {
      strokeStyle: theme.annotationCanvas.canvasOptions.area.strokeStyle,
    },
    object: {
      threshold: 15,
      resizeCircle: {
        radius: 15,
        lineWidth: 2,
        globalAlpha: 1,
        globalAlphaCurtain: 0.25,
        strokeStyle:
          theme.annotationCanvas.canvasOptions.object.resizeCircle.strokeStyle,
        fillStyle:
          theme.annotationCanvas.canvasOptions.object.resizeCircle.fillStyle,
      },
      globalAlpha: 1,
      lineWidth: 4,
      strokeStyle: theme.annotationCanvas.canvasOptions.object.strokeStyle,
      annotated: {
        globalAlpha: 1,
        lineWidth: 4,
        strokeStyle:
          theme.annotationCanvas.canvasOptions.object.annotated.strokeStyle,
      },
      selected: {
        globalAlpha: 1,
        lineWidth: 4,
        strokeStyle:
          theme.annotationCanvas.canvasOptions.object.selected.strokeStyle,
      },
    },
  });
  const [canvasPosition, setCanvasPosition] = useState({
    ratio: null,
    center: { x: null, y: null },
  });
  const [canvasTempMovePosition, setCanvasTempMovePosition] = useState({
    startX: undefined,
    startY: undefined,
    endX: undefined,
    endY: undefined,
  });
  const [canvasTempResizePosition, setCanvasTempResizePosition] = useState({
    edge: undefined,
    startX: undefined,
    startY: undefined,
    endX: undefined,
    endY: undefined,
  });
  const [canvasTempCreatePosition, setCanvasTempCreatePosition] = useState({
    startX: undefined,
    startY: undefined,
    endX: undefined,
    endY: undefined,
  });
  const [canvasLocalObjects, setCanvasLocalObjects] = useState([]);
  const [
    visibleAnnotationCanvasLegendModal,
    setVisibleAnnotationCanvasLegendModal,
  ] = useState(false);

  const handleAction = (action = {}) => {
    if (action.type === 'LEGEND') {
      setVisibleAnnotationCanvasLegendModal(true);
    } else {
      setVisibleAnnotationCanvasLegendModal(false);
    }
    return true;
  };

  const renderCanvasBase = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const ctx = canvas.getContext('2d');
    ctx.restore();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.save();
  };

  const setCanvasPositions = () => {
    const canvas = canvasRef.current;
    const holder = holderRef.current;
    if (!canvas || !holder) return false;

    canvas.width = holder.getBoundingClientRect().width;
    canvas.height = holder.getBoundingClientRect().height;

    const ratio = Math.min(
      canvas.width / mediaWidth,
      canvas.height / mediaHeight
    );
    setCanvasPosition({
      ratio,
      center: {
        x: (canvas.width - mediaWidth * ratio) / 2,

        y: (canvas.height - mediaHeight * ratio) / 2,
      },
    });
  };

  const renderCanvasArea = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const ctx = canvas.getContext('2d');

    const x = canvasPosition.center.x;
    const y = canvasPosition.center.y;
    const drawWidth = mediaWidth * canvasPosition.ratio;
    const drawHeight = mediaHeight * canvasPosition.ratio;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + drawWidth, y);
    ctx.lineTo(x + drawWidth, y + drawHeight);
    ctx.lineTo(x, y + drawHeight);
    ctx.lineTo(x, y);
    ctx.closePath();
    ctx.strokeStyle = canvasOptions.area.strokeStyle;
    ctx.stroke();
    ctx.restore();
  };

  const renderCanvasObjects = () => {
    if (canvasLocalObjects && canvasLocalObjects.length > 0) {
      const canvasLocalObjectsOrdered =
        selectedObject !== undefined
          ? [...canvasLocalObjects].sort((objectA, objectB) =>
              selectedObject === objectA.id && selectedObject !== objectB.id
                ? 1
                : selectedObject !== objectA.id && selectedObject === objectB.id
                ? -1
                : 0
            )
          : canvasLocalObjects;
      for (let object of canvasLocalObjectsOrdered) {
        renderCanvasObject({
          data: object,
          selected:
            selectedObject !== undefined && selectedObject === object.id
              ? true
              : false,
        });
      }
    }
  };

  const renderCanvasCreateObject = () => {
    if (
      canvasTempCreatePosition.startX !== undefined &&
      canvasTempCreatePosition.startY !== undefined &&
      canvasTempCreatePosition.endX !== undefined &&
      canvasTempCreatePosition.endY !== undefined
    ) {
      renderCanvasObject({
        data: {
          coordinates: [
            {
              x: canvasTempCreatePosition.startX,
              y: canvasTempCreatePosition.startY,
            },
            {
              x: canvasTempCreatePosition.endX,
              y: canvasTempCreatePosition.endY,
            },
          ],
        },
        selected: false,
      });
    }
  };

  const renderCanvasObject = ({ data, selected }) => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const ctx = canvas.getContext('2d');

    const itemWidth =
      (data.coordinates[1].x - data.coordinates[0].x) * canvasPosition.ratio;
    const itemHeight =
      (data.coordinates[1].y - data.coordinates[0].y) * canvasPosition.ratio;
    const pointX =
      canvasPosition.center.x + data.coordinates[0].x * canvasPosition.ratio;
    const pointY =
      canvasPosition.center.y + data.coordinates[0].y * canvasPosition.ratio;
    const startX = pointX;
    const startY = pointY;
    const endX = pointX + itemWidth;
    const endY = pointY + itemHeight;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, startY);
    ctx.lineTo(endX, endY);
    ctx.lineTo(startX, endY);
    ctx.closePath();
    ctx.globalAlpha = canvasOptions.object.globalAlpha;
    ctx.lineWidth = canvasOptions.object.lineWidth;
    ctx.strokeStyle =
      data.tags && Object.keys(data.tags).length > 0
        ? canvasOptions.object.annotated.strokeStyle
        : canvasOptions.object.strokeStyle;
    ctx.stroke();
    ctx.restore();
    if (selected) {
      ctx.save();
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, startY);
      ctx.lineTo(endX, endY);
      ctx.lineTo(startX, endY);
      ctx.closePath();
      ctx.globalAlpha = canvasOptions.object.selected.globalAlpha;
      ctx.lineWidth = canvasOptions.object.selected.lineWidth;
      ctx.strokeStyle = canvasOptions.object.selected.strokeStyle;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      const circleRadius = canvasOptions.object.resizeCircle.radius;
      const distanceX = Math.abs(startX - endX);
      const distanceY = Math.abs(startY - endY);

      if (distanceX > circleRadius && distanceY > circleRadius) {
        ctx.save();
        ctx.strokeStyle = canvasOptions.object.resizeCircle.strokeStyle;
        ctx.fillStyle = canvasOptions.object.resizeCircle.fillStyle;
        ctx.lineWidth = canvasOptions.object.resizeCircle.lineWidth;
        ctx.beginPath();
        ctx.arc(startX, startY, circleRadius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlpha;
        ctx.stroke();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlphaCurtain;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(endX, startY, circleRadius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlpha;
        ctx.stroke();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlphaCurtain;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(startX, endY, circleRadius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlpha;
        ctx.stroke();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlphaCurtain;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(endX, endY, circleRadius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlpha;
        ctx.stroke();
        ctx.globalAlpha = canvasOptions.object.resizeCircle.globalAlphaCurtain;
        ctx.fill();
        ctx.restore();
      }
    }
  };

  const handleTapEvent = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const canvasBounds = canvas.getBoundingClientRect();

    const pointX = e.center.x - canvasBounds.left;
    const pointY = e.center.y - canvasBounds.top;

    if (!focusObject(findClosestObject({ pointX, pointY }))) return false;
    return true;
  };

  const handleKeyupEvent = (e) => {
    if ((e.key === 'l' || e.key === 'L') && handleAction({ type: 'LEGEND' }))
      return true;
    if (e.key === 'Delete' && deleteObject()) return true;
    return true;
  };

  const handlePanStartEvent = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const canvasBounds = canvas.getBoundingClientRect();

    const pointX = e.center.x - canvasBounds.left;
    const pointY = e.center.y - canvasBounds.top;

    startResizeObject(pointX, pointY) ||
      startMoveObject() ||
      startCreateObject(pointX, pointY);
  };

  const handlePanEvent = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const canvasBounds = canvas.getBoundingClientRect();

    const pointX = e.center.x - canvasBounds.left;
    const pointY = e.center.y - canvasBounds.top;

    const moveX = e.deltaX;
    const moveY = e.deltaY;
    resizeObject(moveX, moveY) ||
      moveObject(moveX, moveY) ||
      createObject(pointX, pointY);
  };

  const handlePanEndEvent = () => {
    endResizeObject() || endMoveObject() || endCreateObject();
  };

  const focusObject = (object) => {
    if (object)
      handleSelectObject(
        selectedObject !== undefined && selectedObject === object.id
          ? undefined
          : object.id
      );
    else handleSelectObject(undefined);
    return object ? true : false;
  };

  const findClosestObject = ({ pointX, pointY }) => {
    if (canvasLocalObjects && canvasLocalObjects.length > 0) {
      for (let object of canvasLocalObjects) {
        const objectPosition = {
          startX:
            canvasPosition.center.x +
            object.coordinates[0].x * canvasPosition.ratio -
            canvasOptions.object.threshold * canvasPosition.ratio,
          startY:
            canvasPosition.center.y +
            object.coordinates[0].y * canvasPosition.ratio -
            canvasOptions.object.threshold * canvasPosition.ratio,
          endX:
            canvasPosition.center.x +
            object.coordinates[1].x * canvasPosition.ratio +
            canvasOptions.object.threshold * canvasPosition.ratio,
          endY:
            canvasPosition.center.y +
            object.coordinates[1].y * canvasPosition.ratio +
            canvasOptions.object.threshold * canvasPosition.ratio,
        };

        if (
          pointX >= objectPosition.startX &&
          pointX <= objectPosition.endX &&
          pointY >= objectPosition.startY &&
          pointY <= objectPosition.endY
        )
          return object;
      }
    }
    return undefined;
  };

  const startMoveObject = () => {
    if (
      canvasLocalObjects &&
      canvasLocalObjects.length > 0 &&
      selectedObject !== undefined
    ) {
      const object = canvasLocalObjects.find(
        (object) => object.id === selectedObject
      );
      if (object) {
        handleSetVisibleImageMenu(false);
        setCanvasTempMovePosition({
          startX: object.coordinates[0].x,
          startY: object.coordinates[0].y,
          endX: object.coordinates[1].x,
          endY: object.coordinates[1].y,
        });
        return true;
      }
    }
    return false;
  };

  const moveObject = (moveX, moveY) => {
    if (
      canvasTempMovePosition.startX !== undefined &&
      canvasTempMovePosition.startY !== undefined &&
      canvasTempMovePosition.endX !== undefined &&
      canvasTempMovePosition.endY !== undefined &&
      selectedObject !== undefined
    ) {
      const object = canvasLocalObjects.find(
        (object) => object.id === selectedObject
      );
      if (object) {
        const pointTopLeft = {
          x: canvasTempMovePosition.startX + moveX / canvasPosition.ratio,
          y: canvasTempMovePosition.startY + moveY / canvasPosition.ratio,
        };
        const pointBottomRight = {
          x: canvasTempMovePosition.endX + moveX / canvasPosition.ratio,
          y: canvasTempMovePosition.endY + moveY / canvasPosition.ratio,
        };

        const newObject = Object.assign({}, object);
        newObject.coordinates = [
          {
            x: checkSafeXAreaPosition(pointTopLeft, pointBottomRight)
              ? pointTopLeft.x
              : pointTopLeft.x < 0
              ? 0
              : pointBottomRight.x > mediaWidth
              ? mediaWidth - pointBottomRight.x + pointTopLeft.x
              : object.coordinates[0].x,
            y: checkSafeYAreaPosition(pointTopLeft, pointBottomRight)
              ? pointTopLeft.y
              : pointTopLeft.y < 0
              ? 0
              : pointBottomRight.y > mediaHeight
              ? mediaHeight - pointBottomRight.y + pointTopLeft.y
              : object.coordinates[0].y,
          },
          {
            x: checkSafeXAreaPosition(pointTopLeft, pointBottomRight)
              ? pointBottomRight.x
              : pointTopLeft.x < 0
              ? pointBottomRight.x - pointTopLeft.x
              : pointBottomRight.x > mediaWidth
              ? mediaWidth
              : object.coordinates[1].x,
            y: checkSafeYAreaPosition(pointTopLeft, pointBottomRight)
              ? pointBottomRight.y
              : pointTopLeft.y < 0
              ? pointBottomRight.y - pointTopLeft.y
              : pointBottomRight.y > mediaHeight
              ? mediaHeight
              : object.coordinates[1].y,
          },
        ];

        setCanvasLocalObjects([
          ...canvasLocalObjects.map((object) =>
            object.id === newObject.id ? newObject : object
          ),
        ]);
        return true;
      }
    }
    return false;
  };

  const endMoveObject = () => {
    if (
      (canvasTempMovePosition.startX !== undefined ||
        canvasTempMovePosition.startY !== undefined ||
        canvasTempMovePosition.endX !== undefined ||
        canvasTempMovePosition.endY !== undefined) &&
      selectedObject !== undefined
    ) {
      const object = canvasLocalObjects.find(
        (object) => object.id === selectedObject
      );
      if (object) {
        handleChangeAnnotation('OBJECT_CHANGE', {
          startX: Math.round(object.coordinates[0].x),
          startY: Math.round(object.coordinates[0].y),
          endX: Math.round(object.coordinates[1].x),
          endY: Math.round(object.coordinates[1].y),
        });
        setCanvasTempMovePosition({
          startX: undefined,
          startY: undefined,
          endX: undefined,
          endY: undefined,
        });
        handleSetVisibleImageMenu(true);
        return true;
      }
    }
    return false;
  };

  const startResizeObject = (canvasPointX, canvasPointY) => {
    if (
      canvasLocalObjects &&
      canvasLocalObjects.length > 0 &&
      selectedObject !== undefined
    ) {
      const object = canvasLocalObjects.find(
        (object) => object.id === selectedObject
      );
      if (object) {
        const pointX =
          (canvasPointX - canvasPosition.center.x) / canvasPosition.ratio;
        const pointY =
          (canvasPointY - canvasPosition.center.y) / canvasPosition.ratio;

        const edge = getEdgeInsideObjectEdgesRadius(object, pointX, pointY);
        if (typeof edge === 'number') {
          handleSetVisibleImageMenu(false);
          setCanvasTempResizePosition({
            edge,
            startX: object.coordinates[0].x,
            startY: object.coordinates[0].y,
            endX: object.coordinates[1].x,
            endY: object.coordinates[1].y,
          });
          return true;
        }
      }
    }
    return false;
  };

  const getEdgeInsideObjectEdgesRadius = (object, pointX, pointY) => {
    const circleRadius = canvasOptions.object.resizeCircle.radius * 2;
    if (
      Math.sqrt(
        (object.coordinates[0].x - pointX) *
          (object.coordinates[0].x - pointX) +
          (object.coordinates[0].y - pointY) *
            (object.coordinates[0].y - pointY)
      ) <
      circleRadius / canvasPosition.ratio
    )
      return 0;
    else if (
      Math.sqrt(
        (object.coordinates[1].x - pointX) *
          (object.coordinates[1].x - pointX) +
          (object.coordinates[0].y - pointY) *
            (object.coordinates[0].y - pointY)
      ) <
      circleRadius / canvasPosition.ratio
    )
      return 1;
    else if (
      Math.sqrt(
        (object.coordinates[1].x - pointX) *
          (object.coordinates[1].x - pointX) +
          (object.coordinates[1].y - pointY) *
            (object.coordinates[1].y - pointY)
      ) <
      circleRadius / canvasPosition.ratio
    )
      return 2;
    else if (
      Math.sqrt(
        (object.coordinates[0].x - pointX) *
          (object.coordinates[0].x - pointX) +
          (object.coordinates[1].y - pointY) *
            (object.coordinates[1].y - pointY)
      ) <
      circleRadius / canvasPosition.ratio
    )
      return 3;

    return false;
  };

  const resizeObject = (moveX, moveY) => {
    if (
      canvasTempResizePosition.startX !== undefined &&
      canvasTempResizePosition.startY !== undefined &&
      canvasTempResizePosition.endX !== undefined &&
      canvasTempResizePosition.endY !== undefined &&
      selectedObject !== undefined
    ) {
      const object = canvasLocalObjects.find(
        (object) => object.id === selectedObject
      );
      if (object) {
        const pointTopLeft = {
          x:
            canvasTempResizePosition.startX +
            (canvasTempResizePosition.edge === 0 ||
            canvasTempResizePosition.edge === 3
              ? moveX
              : 0) /
              canvasPosition.ratio,
          y:
            canvasTempResizePosition.startY +
            (canvasTempResizePosition.edge === 0 ||
            canvasTempResizePosition.edge === 1
              ? moveY
              : 0) /
              canvasPosition.ratio,
        };
        const pointBottomRight = {
          x:
            canvasTempResizePosition.endX +
            (canvasTempResizePosition.edge === 1 ||
            canvasTempResizePosition.edge === 2
              ? moveX
              : 0) /
              canvasPosition.ratio,
          y:
            canvasTempResizePosition.endY +
            (canvasTempResizePosition.edge === 2 ||
            canvasTempResizePosition.edge === 3
              ? moveY
              : 0) /
              canvasPosition.ratio,
        };

        const newObject = Object.assign({}, object);
        const newPointTopLeft = {
          x: checkSafeXAreaPosition(pointTopLeft, pointBottomRight)
            ? pointTopLeft.x
            : pointTopLeft.x < 0 || pointBottomRight.x < 0
            ? 0
            : object.coordinates[0].x,
          y: checkSafeYAreaPosition(pointTopLeft, pointBottomRight)
            ? pointTopLeft.y
            : pointTopLeft.y < 0 || pointBottomRight.y < 0
            ? 0
            : object.coordinates[0].y,
        };
        const newPointBottomRight = {
          x: checkSafeXAreaPosition(pointTopLeft, pointBottomRight)
            ? pointBottomRight.x
            : pointTopLeft.x > mediaWidth || pointBottomRight.x > mediaWidth
            ? mediaWidth
            : object.coordinates[1].x,
          y: checkSafeYAreaPosition(pointTopLeft, pointBottomRight)
            ? pointBottomRight.y
            : pointTopLeft.y > mediaHeight || pointBottomRight.y > mediaHeight
            ? mediaHeight
            : object.coordinates[1].y,
        };
        newObject.coordinates = [
          {
            x: Math.min(newPointTopLeft.x, newPointBottomRight.x),
            y: Math.min(newPointTopLeft.y, newPointBottomRight.y),
          },
          {
            x: Math.max(newPointTopLeft.x, newPointBottomRight.x),
            y: Math.max(newPointTopLeft.y, newPointBottomRight.y),
          },
        ];
        setCanvasLocalObjects([
          ...canvasLocalObjects.map((object) =>
            object.id === newObject.id ? newObject : object
          ),
        ]);
        return true;
      }
    }
    return false;
  };

  const endResizeObject = () => {
    if (
      (canvasTempResizePosition.startX !== undefined ||
        canvasTempResizePosition.startY !== undefined ||
        canvasTempResizePosition.endX !== undefined ||
        canvasTempResizePosition.endY !== undefined) &&
      selectedObject !== undefined
    ) {
      const object = canvasLocalObjects.find(
        (object) => object.id === selectedObject
      );
      if (object) {
        handleChangeAnnotation('OBJECT_CHANGE', {
          startX: Math.round(object.coordinates[0].x),
          startY: Math.round(object.coordinates[0].y),
          endX: Math.round(object.coordinates[1].x),
          endY: Math.round(object.coordinates[1].y),
        });
        setCanvasTempResizePosition({
          edge: undefined,
          startX: undefined,
          startY: undefined,
          endX: undefined,
          endY: undefined,
        });
        handleSetVisibleImageMenu(true);
        return true;
      }
    }
    return false;
  };

  const startCreateObject = (canvasPointX, canvasPointY) => {
    if (selectedObject === undefined) {
      const pointX =
        (canvasPointX - canvasPosition.center.x) / canvasPosition.ratio;
      const pointY =
        (canvasPointY - canvasPosition.center.y) / canvasPosition.ratio;

      handleSetVisibleImageMenu(false);
      setCanvasTempCreatePosition({
        startX: pointX,
        startY: pointY,
        endX: undefined,
        endY: undefined,
      });
      return true;
    }
    return false;
  };

  const createObject = (canvasPointX, canvasPointY) => {
    if (
      canvasTempCreatePosition.startX !== undefined &&
      canvasTempCreatePosition.startY !== undefined
    ) {
      const pointTopLeft = {
        x: canvasTempCreatePosition.startX,
        y: canvasTempCreatePosition.startY,
      };

      const pointBottomRight = {
        x: (canvasPointX - canvasPosition.center.x) / canvasPosition.ratio,
        y: (canvasPointY - canvasPosition.center.y) / canvasPosition.ratio,
      };

      const newPointTopLeft = {
        x: checkSafeXAreaPosition(pointTopLeft, pointBottomRight)
          ? pointTopLeft.x
          : pointTopLeft.x < 0
          ? 0
          : pointTopLeft.x > mediaWidth
          ? mediaWidth
          : pointTopLeft.x,
        y: checkSafeYAreaPosition(pointTopLeft, pointBottomRight)
          ? pointTopLeft.y
          : pointTopLeft.y < 0
          ? 0
          : pointTopLeft.y > mediaHeight
          ? mediaHeight
          : pointTopLeft.y,
      };
      const newPointBottomRight = {
        x: checkSafeXAreaPosition(pointTopLeft, pointBottomRight)
          ? pointBottomRight.x
          : pointBottomRight.x < 0
          ? 0
          : pointBottomRight.x > mediaWidth
          ? mediaWidth
          : pointBottomRight.x,
        y: checkSafeYAreaPosition(pointTopLeft, pointBottomRight)
          ? pointBottomRight.y
          : pointBottomRight.y < 0
          ? 0
          : pointBottomRight.y > mediaHeight
          ? mediaHeight
          : pointBottomRight.y,
      };
      setCanvasTempCreatePosition({
        startX: newPointTopLeft.x,
        startY: newPointTopLeft.y,
        endX: newPointBottomRight.x,
        endY: newPointBottomRight.y,
      });

      return true;
    }
    return false;
  };

  const endCreateObject = () => {
    if (
      canvasTempCreatePosition.startX !== undefined ||
      canvasTempCreatePosition.startY !== undefined ||
      canvasTempCreatePosition.endX !== undefined ||
      canvasTempCreatePosition.endY !== undefined
    ) {
      handleChangeAnnotation('OBJECT_CREATE', {
        startX: Math.round(
          Math.min(
            canvasTempCreatePosition.startX,
            canvasTempCreatePosition.endX
          )
        ),
        startY: Math.round(
          Math.min(
            canvasTempCreatePosition.startY,
            canvasTempCreatePosition.endY
          )
        ),
        endX: Math.round(
          Math.max(
            canvasTempCreatePosition.startX,
            canvasTempCreatePosition.endX
          )
        ),
        endY: Math.round(
          Math.max(
            canvasTempCreatePosition.startY,
            canvasTempCreatePosition.endY
          )
        ),
      });
      setCanvasTempCreatePosition({
        edge: undefined,
        startX: undefined,
        startY: undefined,
        endX: undefined,
        endY: undefined,
      });
      handleSetVisibleImageMenu(true);
      return true;
    }
    return false;
  };

  const deleteObject = () => {
    if (selectedObject !== undefined) {
      handleChangeAnnotation('OBJECT_DELETE', {});
      return true;
    }
    return false;
  };

  const checkSafeXAreaPosition = (pointTopLeft, pointBottomRight) => {
    if (
      pointTopLeft.x >= 0 &&
      pointTopLeft.x <= mediaWidth &&
      pointBottomRight.x >= 0 &&
      pointBottomRight.x <= mediaWidth
    )
      return true;
    return false;
  };

  const checkSafeYAreaPosition = (pointTopLeft, pointBottomRight) => {
    if (
      pointTopLeft.y >= 0 &&
      pointTopLeft.y <= mediaHeight &&
      pointBottomRight.y >= 0 &&
      pointBottomRight.y <= mediaHeight
    )
      return true;
    return false;
  };

  useEffect(() => {
    window.addEventListener('keyup', handleKeyupEvent);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('keyup', handleKeyupEvent);
    };
    // eslint-disable-next-line
  }, [objects, selectedObject, visibleAnnotationCanvasLegendModal]);

  useEffect(() => {
    setCanvasLocalObjects(objects);
  }, [objects]);

  useEffect(() => {
    setCanvasPositions();
    // eslint-disable-next-line
  }, [canvasSize, mediaWidth, mediaHeight]);

  useEffect(() => {
    let requestAnimationFrameId;
    const renderCanvas = () => {
      renderCanvasBase();
      renderCanvasArea();
      renderCanvasObjects();
      renderCanvasCreateObject();
      requestAnimationFrameId = requestAnimationFrame(renderCanvas);
    };

    renderCanvas();

    return () => {
      if (requestAnimationFrameId)
        cancelAnimationFrame(requestAnimationFrameId);
    };
    // eslint-disable-next-line
  }, [
    canvasPosition,
    canvasLocalObjects,
    canvasTempCreatePosition,
    selectedObject,
    mediaWidth,
    mediaHeight,
  ]);

  return (
    <>
      <Box>
        <Holder ref={holderRef}>
          <Hammer
            direction='DIRECTION_ALL'
            onTap={handleTapEvent}
            onPanStart={handlePanStartEvent}
            onPan={handlePanEvent}
            onPanEnd={handlePanEndEvent}
          >
            <CanvasHolder>
              <Canvas ref={canvasRef} />
            </CanvasHolder>
          </Hammer>
        </Holder>
      </Box>
      {visibleAnnotationCanvasLegendModal && (
        <AnnotationCanvasLegend
          handleClose={() => {
            handleAction();
          }}
        />
      )}
    </>
  );
};

AnnotationCanvas.propTypes = {
  mediaWidth: PropTypes.number,
  mediaHeight: PropTypes.number,
  objects: PropTypes.array,
  selectedObject: PropTypes.number,
  handleSelectObject: PropTypes.func,
  handleChangeAnnotation: PropTypes.func,
  handleSetVisibleImageMenu: PropTypes.func,
};

export default AnnotationCanvas;
